import { Tag, CalendarDays, ZoomIn } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { CONTACT_PRICE, rp, formatDate, packageImageUrl } from "@/lib/api";

const PriceBlock = ({ p }) => {
  if (p.promo_active && p.promo_price != null) {
    return (
      <div className="flex items-baseline gap-2">
        <p className="text-xl font-extrabold text-nanina-green" data-testid={`package-promo-price-${p.id}`}>{rp(p.promo_price)}</p>
        {p.regular_price ? <p className="text-xs text-nanina-muted line-through" data-testid={`package-regular-price-${p.id}`}>{rp(p.regular_price)}</p> : null}
      </div>
    );
  }
  return (
    <p className={p.regular_price ? "text-xl font-extrabold text-nanina-green" : "text-xs text-nanina-muted"} data-testid={`package-price-${p.id}`}>
      {p.regular_price ? rp(p.regular_price) : CONTACT_PRICE}
    </p>
  );
};

const Poster = ({ p, onOpen }) => (
  <button type="button" onClick={onOpen} className="group relative block w-full aspect-[4/5] bg-nanina-skysoft/60 overflow-hidden" aria-label={`Perbesar gambar ${p.name}`} data-testid={`package-image-${p.id}`}>
    <img src={packageImageUrl(p)} alt={p.name} loading="lazy" className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.02]" />
    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold text-nanina-green"><ZoomIn className="h-3 w-3" /> Perbesar</span>
  </button>
);

const PackageCard = ({ p, catName, onOpen }) => {
  const { openInquiry } = useSite();
  const showText = !(p.has_image && p.hide_text);
  return (
    <article className="snap-start shrink-0 w-[85%] sm:w-[48%] md:w-auto rounded-3xl bg-white border border-nanina-line overflow-hidden flex flex-col" data-testid={`package-card-${p.id}`}>
      {p.has_image && <Poster p={p} onOpen={onOpen} />}
      <div className="p-5 flex flex-col gap-2.5 flex-1">
        {p.promo_active && (
          <span className="self-start inline-flex items-center gap-1 rounded-full bg-nanina-butter/70 text-nanina-green text-[11px] font-bold px-2.5 py-1" data-testid={`package-promo-badge-${p.id}`}>
            <Tag className="h-3 w-3" /> {p.discount_label || "Promo"}
          </span>
        )}
        <h3 className="text-lg font-extrabold tracking-tight text-nanina-green">{p.name}</h3>
        {showText && p.description && <p className="text-sm text-nanina-muted line-clamp-3">{p.description}</p>}
        {p.category_ids?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {p.category_ids.map((id) => catName[id] && <span key={id} className="text-[10px] rounded-full bg-nanina-skysoft px-2 py-0.5 text-nanina-green">{catName[id]}</span>)}
          </div>
        )}
        <PriceBlock p={p} />
        {p.promo_active && (
          <p className="flex items-center gap-1.5 text-[11px] text-nanina-muted" data-testid={`package-validity-${p.id}`}>
            <CalendarDays className="h-3.5 w-3.5" /> Berlaku {formatDate(p.promo_start)} – {formatDate(p.promo_end)}
          </p>
        )}
        {showText && p.terms?.length > 0 && (
          <ul className="text-[11px] text-nanina-muted list-disc pl-4 space-y-0.5" data-testid={`package-terms-${p.id}`}>
            {p.terms.slice(0, 3).map((t) => <li key={t}>{t}</li>)}
          </ul>
        )}
        <button onClick={() => openInquiry(`Paket: ${p.name}`)} className="mt-auto rounded-full bg-nanina-green text-white text-sm font-semibold px-5 py-2.5 hover:bg-nanina-greendark transition-colors" data-testid={`package-ask-${p.id}`}>
          Tanya paket ini
        </button>
      </div>
    </article>
  );
};

export const Packages = () => {
  const { site, openLightbox } = useSite();
  if (!site.packages?.length) return null;
  const catName = Object.fromEntries(site.categories.map((c) => [c.id, c.name]));
  const withImg = site.packages.filter((p) => p.has_image);
  const slides = withImg.map((p) => ({ id: p.id, src: packageImageUrl(p), caption: p.name }));
  return (
    <section id="paket" className="pb-12 sm:pb-16" data-testid="packages-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-nanina-fresh">Paket & Promo</p>
        <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-nanina-green">Paket & Promo Nanina Care</h2>
        <p className="mt-2 text-sm text-nanina-muted">Harga dan promo yang sedang berlaku. Ketentuan lengkap dikonfirmasi admin.</p>
        <div className="mt-5 flex md:grid md:grid-cols-3 gap-3 overflow-x-auto nn-scroll-x snap-x pb-1">
          {site.packages.map((p) => <PackageCard key={p.id} p={p} catName={catName} onOpen={() => openLightbox(slides, withImg.indexOf(p))} />)}
        </div>
      </div>
    </section>
  );
};
